/**
 * AgentToolGatesEditor — per-agent tool gating configuration.
 *
 * Renders a card that lets an operator configure when each of an agent's
 * tools becomes available to the LLM. "Gates" are conditions (patient
 * input received, prior tool succeeded, N turns elapsed, etc.) that
 * must ALL be true before the gated tool is exposed to the model's tool
 * list. Enforced at runtime by pipeline.js / chain-text-controller.js.
 *
 * Used on two surfaces:
 *   • Sub-tenant → Agents → Built-in Agents → click an agent → below the prompt editor
 *   • Sub-tenant → Agents → click a library agent → below the prompt editor
 *
 * Props:
 *   agentTools         : string[]   — declared tool names on the agent (options)
 *   platformDefaults   : array      — read-only reference gates (from library/JS)
 *   value              : array|null — current tenant override (null = use defaults)
 *   onChange           : (val) => void — val is the new override array, or null to inherit
 *   readOnly           : bool       — live mode hides all edit controls
 *   agentLabel         : string     — shown in the header
 *
 * Storage: the enclosing page is responsible for persisting. This
 * component is pure — it just calls onChange(newGates) when the user
 * edits. onChange(null) means "use platform defaults" (clear the
 * override).
 */

import React from "react";

// ── Condition type registry (mirrors runtime GATE_CONDITION_TYPES) ──
const CONDITION_TYPES = [
  {
    type: "patient_input",
    label: "Patient input received",
    description: "Passes after the patient has spoken at least once in this agent session.",
    params: [],
  },
  {
    type: "prior_tool_success",
    label: "Prior tool success",
    description: "Passes after the named tool has returned successfully.",
    params: [{ name: "tool", label: "Tool", type: "string", required: true }],
  },
  {
    type: "prior_any_tool",
    label: "Any prior tool success",
    description: "Passes after any tool has succeeded.",
    params: [],
  },
  {
    type: "min_llm_turns",
    label: "Minimum LLM turns",
    description: "Passes after N LLM iterations.",
    params: [{ name: "n", label: "Minimum turns", type: "integer", required: true, min: 1, max: 20, default: 1 }],
  },
  {
    type: "context_field_present",
    label: "Context field present",
    description: "Passes when a named context field (e.g. patientId) is non-empty.",
    params: [{ name: "field", label: "Field name", type: "string", required: true }],
  },
];

const GATE_MODES = [
  { value: "hide", label: "Hide (enforce)" },
  { value: "warn", label: "Warn (observe only)" },
];

function describeCondition(cond) {
  const spec = CONDITION_TYPES.find((c) => c.type === cond?.type);
  if (!spec) return cond?.type || "unknown";
  switch (cond.type) {
    case "patient_input":         return "patient input";
    case "prior_tool_success":    return `prior tool success: ${cond.tool || "?"}`;
    case "prior_any_tool":        return "any prior tool";
    case "min_llm_turns":         return `min ${cond.n || 1} LLM turn(s)`;
    case "context_field_present": return `context.${cond.field || "?"} present`;
    default:                       return spec.label;
  }
}

export default function AgentToolGatesEditor({
  agentTools = [],
  platformDefaults = [],
  value,
  onChange,
  readOnly = false,
  agentLabel = "this agent",
}) {
  const hasOverride = Array.isArray(value);
  const activeGates = hasOverride ? value : platformDefaults;

  // Grouped by tool so the UI can show "no gates" for ungated tools
  const gatesByTool = {};
  for (const g of (activeGates || [])) {
    if (!g?.tool) continue;
    if (!gatesByTool[g.tool]) gatesByTool[g.tool] = [];
    gatesByTool[g.tool].push(g);
  }

  // ── Mutation helpers ────────────────────────────────────────────
  const startOverride = () => {
    // Copy the platform defaults as the starting point for customization.
    onChange(JSON.parse(JSON.stringify(platformDefaults || [])));
  };
  const clearOverride = () => onChange(null);

  const updateGate = (tool, index, updater) => {
    const next = JSON.parse(JSON.stringify(value || []));
    const idxs = next
      .map((g, i) => (g.tool === tool ? i : -1))
      .filter((i) => i >= 0);
    const targetIdx = idxs[index];
    if (targetIdx == null) return;
    next[targetIdx] = updater(next[targetIdx]) || next[targetIdx];
    onChange(next);
  };

  const addGate = (tool) => {
    const next = JSON.parse(JSON.stringify(value || []));
    next.push({
      tool,
      mode: "hide",
      when_all: [{ type: "patient_input" }],
    });
    onChange(next);
  };

  const removeGate = (tool, index) => {
    const next = JSON.parse(JSON.stringify(value || []));
    const flatIdxs = next
      .map((g, i) => (g.tool === tool ? i : -1))
      .filter((i) => i >= 0);
    const targetIdx = flatIdxs[index];
    if (targetIdx == null) return;
    next.splice(targetIdx, 1);
    onChange(next);
  };

  const addCondition = (tool, gateIndex) => {
    updateGate(tool, gateIndex, (g) => {
      g.when_all = [...(g.when_all || []), { type: "patient_input" }];
      return g;
    });
  };

  const updateCondition = (tool, gateIndex, condIndex, updater) => {
    updateGate(tool, gateIndex, (g) => {
      const conds = [...(g.when_all || [])];
      conds[condIndex] = updater(conds[condIndex]) || conds[condIndex];
      g.when_all = conds;
      return g;
    });
  };

  const removeCondition = (tool, gateIndex, condIndex) => {
    updateGate(tool, gateIndex, (g) => {
      const conds = [...(g.when_all || [])];
      conds.splice(condIndex, 1);
      g.when_all = conds;
      return g;
    });
  };

  // ── Render ──────────────────────────────────────────────────────
  return (
    <div className="card" style={{ marginTop: 16, borderLeft: "4px solid #6A4C93" }}>
      <div className="card-header" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <h3>
          Tool Gates{" "}
          <span className="muted small" style={{ fontWeight: 400, marginLeft: 6 }}>
            chain rules
          </span>
        </h3>
        {!readOnly && (
          <div style={{ display: "flex", gap: 8 }}>
            {!hasOverride ? (
              <>
                <span className="badge" style={{ background: "#e8f5e9", color: "#2e7d32", fontSize: 11 }}>
                  using platform defaults
                </span>
                <button className="btn btn-sm" onClick={startOverride}>
                  Customize
                </button>
              </>
            ) : (
              <>
                <span className="badge" style={{ background: "#fff3e0", color: "#ef6c00", fontSize: 11 }}>
                  custom override
                </span>
                <button
                  className="btn btn-sm"
                  onClick={clearOverride}
                  title="Reset to platform defaults"
                >
                  Reset to defaults
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <p className="muted small" style={{ lineHeight: 1.5, marginBottom: 16 }}>
        Hold back specific tools until their conditions are met. Gated tools
        are removed from the LLM's tool list until all conditions pass, so
        the model physically cannot call them prematurely. Rules are enforced
        at runtime by the voice/text pipeline.
      </p>

      {/* Per-tool gate editor */}
      {agentTools.length === 0 ? (
        <div className="muted small" style={{ fontStyle: "italic", padding: 8 }}>
          This agent has no tools declared. Add tools in the Agent Config section first.
        </div>
      ) : (
        <div>
          {agentTools.map((toolName) => {
            const toolGates = gatesByTool[toolName] || [];
            return (
              <div
                key={toolName}
                style={{
                  marginBottom: 12,
                  padding: 10,
                  background: toolGates.length > 0 ? "#faf7fd" : "#fafafa",
                  border: "1px solid var(--color-border)",
                  borderRadius: 4,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <code style={{ fontWeight: 600, fontSize: 13 }}>{toolName}</code>
                  {toolGates.length === 0 ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span className="muted small" style={{ fontStyle: "italic" }}>
                        no gates — always available
                      </span>
                      {hasOverride && !readOnly && (
                        <button className="btn btn-sm" onClick={() => addGate(toolName)}>
                          + add gate
                        </button>
                      )}
                    </div>
                  ) : (
                    hasOverride && !readOnly && (
                      <button className="btn btn-sm" onClick={() => addGate(toolName)}>
                        + add gate
                      </button>
                    )
                  )}
                </div>

                {toolGates.map((gate, gateIdx) => (
                  <div
                    key={gateIdx}
                    style={{
                      marginTop: 8,
                      padding: 8,
                      background: "#fff",
                      border: "1px solid var(--color-border)",
                      borderRadius: 4,
                    }}
                  >
                    <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6 }}>
                      <label className="small" style={{ fontWeight: 600 }}>Mode:</label>
                      <select
                        value={gate.mode || "hide"}
                        onChange={(e) => updateGate(toolName, gateIdx, (g) => { g.mode = e.target.value; return g; })}
                        disabled={readOnly || !hasOverride}
                        style={{ fontSize: 12, padding: "2px 6px" }}
                      >
                        {GATE_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                      {hasOverride && !readOnly && (
                        <button
                          className="btn btn-sm"
                          style={{ marginLeft: "auto", color: "#c62828" }}
                          onClick={() => removeGate(toolName, gateIdx)}
                        >
                          × remove gate
                        </button>
                      )}
                    </div>

                    <div className="small" style={{ fontWeight: 600, marginBottom: 4 }}>
                      Hide until ALL of these are true:
                    </div>
                    {(gate.when_all || []).map((cond, condIdx) => (
                      <div
                        key={condIdx}
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          padding: "4px 6px",
                          marginBottom: 4,
                          background: "#f5f0fa",
                          borderRadius: 3,
                        }}
                      >
                        <select
                          value={cond.type || "patient_input"}
                          onChange={(e) => {
                            const type = e.target.value;
                            updateCondition(toolName, gateIdx, condIdx, () => ({ type }));
                          }}
                          disabled={readOnly || !hasOverride}
                          style={{ fontSize: 12, padding: "2px 6px" }}
                        >
                          {CONDITION_TYPES.map((c) => (
                            <option key={c.type} value={c.type}>{c.label}</option>
                          ))}
                        </select>

                        {/* Params per condition type */}
                        {(CONDITION_TYPES.find(c => c.type === cond.type)?.params || []).map((param) => (
                          param.type === "integer" ? (
                            <input
                              key={param.name}
                              type="number"
                              min={param.min || 1}
                              max={param.max || 999}
                              value={cond[param.name] ?? param.default ?? 1}
                              onChange={(e) => {
                                const v = parseInt(e.target.value, 10) || (param.default || 1);
                                updateCondition(toolName, gateIdx, condIdx, (c) => ({ ...c, [param.name]: v }));
                              }}
                              disabled={readOnly || !hasOverride}
                              style={{ width: 60, fontSize: 12, padding: "2px 4px" }}
                              title={param.label}
                            />
                          ) : (
                            <input
                              key={param.name}
                              type="text"
                              placeholder={param.label}
                              value={cond[param.name] || ""}
                              onChange={(e) => {
                                const v = e.target.value;
                                updateCondition(toolName, gateIdx, condIdx, (c) => ({ ...c, [param.name]: v }));
                              }}
                              disabled={readOnly || !hasOverride}
                              style={{ fontSize: 12, padding: "2px 6px", fontFamily: "var(--font-mono)", minWidth: 160 }}
                              title={param.label}
                            />
                          )
                        ))}

                        {hasOverride && !readOnly && (gate.when_all || []).length > 1 && (
                          <button
                            onClick={() => removeCondition(toolName, gateIdx, condIdx)}
                            style={{ marginLeft: "auto", border: "none", background: "transparent", color: "#c62828", cursor: "pointer", fontSize: 14 }}
                            title="Remove condition"
                          >×</button>
                        )}
                      </div>
                    ))}
                    {hasOverride && !readOnly && (
                      <button
                        className="btn btn-sm"
                        onClick={() => addCondition(toolName, gateIdx)}
                        style={{ marginTop: 4 }}
                      >
                        + add condition
                      </button>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Platform defaults reference */}
      {platformDefaults.length > 0 && (
        <div style={{ marginTop: 16, padding: 10, background: "#f5f5f5", borderRadius: 4, fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--color-text-muted)" }}>
            Platform defaults ({platformDefaults.length} gate{platformDefaults.length === 1 ? "" : "s"})
          </div>
          {platformDefaults.map((g, i) => (
            <div key={i} style={{ padding: "2px 0", fontFamily: "var(--font-mono)" }}>
              <code>{g.tool}</code>{" "}
              <span className="muted small">[{g.mode || "hide"}]</span>{" "}
              <span className="muted small">
                until {(g.when_all || []).map(describeCondition).join(" AND ")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
